import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FlowBaseUrlEnum } from '@constants/app.constants';
import { BaseError, ErrorType } from '@models/app.models';
import { CountryPhoneCode } from '@models/cms/app.model';
import { PaymentForm } from '@models/form/form.models';
import { PaymentFlow } from '@models/payment.model';
import { ApplyBookingCreditRequestv2 } from '@models/requests/credit-file-payment-request.model';
import {
  ApplePaySessionRequest,
  ExternalPaymentRequest,
} from '@models/requests/external-payment-request.model';
import { VoucherPaymentRequest } from '@models/requests/voucher-payment-request.model';
import { ApiResponse } from '@models/responses/api-response.model';
import { N0CreditAccount } from '@models/responses/credit-file-information.model';
import { VoucherInformation } from '@models/responses/voucher-information.model';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { PlatformService } from '@services/platform/platform.service';
import {
  createPhoneNumberFromParts,
  getPaymentMethodCodeFromCardNumber,
  PAYMENT_METHOD_FULL_REGEX,
} from '@utils/data-utils';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private static readonly CONTROLLER_ROOT = 'v1/payments';
  private static readonly VOUCHER_ROOT = '/voucher';
  private static readonly CREDIT_FILE_ROOT = '/bookingcredit';

  constructor(
    private httpClient: HttpClient,
    private platformService: PlatformService,
  ) {}

  verifyVoucher(
    voucherCode: string,
  ): Observable<ApiResponse<VoucherInformation>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PaymentService.CONTROLLER_ROOT}${PaymentService.VOUCHER_ROOT}/${voucherCode}`;
    return this.httpClient.get<ApiResponse<VoucherInformation>>(endpoint);
  }

  submitVoucher(voucher: VoucherPaymentRequest): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PaymentService.CONTROLLER_ROOT}${PaymentService.VOUCHER_ROOT}`;
    return this.httpClient.post<ApiResponse<null>>(endpoint, voucher);
  }

  verifyCreditFile(
    recordLocator: string,
    lastName: string,
    currencyCode: string,
  ): Observable<ApiResponse<N0CreditAccount>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PaymentService.CONTROLLER_ROOT}${PaymentService.CREDIT_FILE_ROOT}/${recordLocator}/${lastName}/${currencyCode}`;
    return this.httpClient.get<ApiResponse<N0CreditAccount>>(endpoint);
  }

  submitCreditFile(
    voucher: ApplyBookingCreditRequestv2,
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PaymentService.CONTROLLER_ROOT}${PaymentService.CREDIT_FILE_ROOT}`;
    return this.httpClient.post<ApiResponse<null>>(endpoint, voucher);
  }

  submitPayment(
    payment: ExternalPaymentRequest,
  ): Observable<ApiResponse<null>> {
    return this.submitPayments([payment]);
  }

  submitPayments(
    payments: ExternalPaymentRequest[],
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PaymentService.CONTROLLER_ROOT}`;
    return this.httpClient.post<ApiResponse<null>>(endpoint, payments);
  }

  refundPayments(): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PaymentService.CONTROLLER_ROOT}/refund`;
    return this.httpClient.post<ApiResponse<null>>(endpoint, {});
  }

  getApplePaySession(
    request: ApplePaySessionRequest,
  ): Observable<ApiResponse<unknown>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PaymentService.CONTROLLER_ROOT}/applepaysession`;
    return this.httpClient.post<ApiResponse<unknown>>(endpoint, request);
  }

  /**
   *
   * @param currencyCode The currency code to submit the payment with
   * @param amount The balance paid on the booking
   * @param form The form data that was submitted by the user
   * @param paymentMethodTypes The payment method type resources
   * @returns The request data to submit a payment
   * @throws {@link BaseError}
   * Thrown if the card number does not match an existing payment method
   */
  transformFormDataToSubmitPaymentRequest(
currencyCode: string, cultureCode: string, amount: number | string, form: PaymentForm, flow: FlowBaseUrlEnum, paymentFlow: PaymentFlow | null, phoneCodes: CountryPhoneCode[]): ExternalPaymentRequest {
    const digitsRegex = /^[\d]{13,16}$/g;
    // uplift is not index-based, thus cardExpirationMonth equals month number while month picker is index-based and we add + 1 to create correct SkayPay payload
    const expirationMonth = paymentFlow === PaymentFlow.UPLIFT_LOAN ? Number(form.cardExpirationMonth) : Number(form.cardExpirationMonth) + 1;
    const twoDigitMonth = expirationMonth.toFixed(0).padStart(2, '0');
    const fourDigitYear = form.cardExpirationYear.toFixed(0);
    const cardNumberTrimmed = form.cardNumber.replace(/[ |-]/g, '');
    const paymentMethodCode = getPaymentMethodCodeFromCardNumber(
      form.cardNumber,
      PAYMENT_METHOD_FULL_REGEX,
    );

    if (!paymentMethodCode || !digitsRegex.test(cardNumberTrimmed)) {
      throw new BaseError(ErrorType.SubmitPaymentFailure);
    }
    const phone = this.getPhoneNumberFromForm(form, phoneCodes);
    return {
      bookingFlow: flow,
      amount: Number(amount),
      currencyCode,
      paymentMethodCode: paymentMethodCode,
      paymentFields: {
        // we know this isn't null because we tested the regex above and it passed
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        accountNumber: cardNumberTrimmed.match(digitsRegex)!.join(''),
        expirationDate: `${twoDigitMonth}${fourDigitYear}`,
        name: form.nameOnCard,
        verificationCode: form.cardCvv,
        address1: form.billingAddressLineOne,
        address2: form.billingAddressLineTwo,
        city: form.billingCity,
        stateOrProvince: form.billingState,
        postalCode: '' + form.billingZipCode,
        country: form.billingCountry,
        billingEmail: form.email,
        billingPhone: phone,
        // 3DS
        acceptHeader: 'text/html, application/json',
        colorDepth: String(window.screen.colorDepth),
        language: cultureCode,
        screenHeight: String(window.screen.height),
        screenWidth: String(window.screen.width),
        timezone: String(new Date().getTimezoneOffset()),
        returnUrl: `${window.location.origin}/processing`,
      },
      platforms: this.platformService.platforms(),
      isUplift: paymentFlow === PaymentFlow.UPLIFT_LOAN
    };
  }

  /**
   *
   * @param currencyCode The currency code to submit the voucher with
   * @param amount The amount paid by the voucher
   * @param form The form data that was submitted by the user
   * @returns The request data to submit a voucher
   */
  transformFormDataToSubmitVoucherRequest(
    currencyCode: string,
    amount: number,
    code: string,
  ): VoucherPaymentRequest {
    return {
      amount,
      currencyCode,
      paymentMethodCode: 'VO',
      referenceCode: code,
      overrideRestrictions: false,
    };
  }

  /**
   *
   * @param currencyCode The currency code to submit the voucher with
   * @param amount The amount paid by the voucher
   * @param form The form data that was submitted by the user
   * @returns The request data to submit a voucher
   */
  transformFormDataToSubmitCreditFileRequest(
    currencyCode: string,
    amount: number,
    recordLocator: string,
    firstName?: string,
    lastName?: string,
    emailAddress?: string,
    departureDate?: Date,
    origin?: string,
    customerNumber?: string,
  ): ApplyBookingCreditRequestv2 {
    return {
      request: {
        currencyCode,
        amount,
      },
      validation: {
        customerNumber,
        emailAddress,
        firstName,
        lastName,
        origin,
        recordLocator,
        departureDate,
      },
    };
  }

  getPhoneNumberFromForm(
    form: PaymentForm,
    phoneCodes: CountryPhoneCode[],
  ): string | undefined {
    if (!form.phoneCode || !form.phone) {
      return;
    }

    const phoneCodeNumber = phoneCodes.find(
      (code) => code.countryCode === form.phoneCode,
    )?.value;
    return createPhoneNumberFromParts(phoneCodeNumber, form.phone);
  }
}
